import React, { useState, useMemo } from 'react';
import { Modal, Table, Button, Tag, Typography, Space, message } from 'antd';
import { WarningOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import type { CartChangeItem, CartResolveBody } from '../api/oilOrders';
import { oilOrdersApi } from '../api/oilOrders';

const { Text } = Typography;

interface Props {
  open: boolean;
  changes: CartChangeItem[];
  taskId?: number | null;
  onClose: () => void;
  onResolved: () => void;
}

const CHANGE_LABELS: Record<string, string> = {
  removed: 'Удалён',
  price_changed: 'Цена изменилась',
  qty_changed: 'Кол-во изменилось',
  price_and_qty_changed: 'Цена и кол-во',
};

const CHANGE_COLORS: Record<string, string> = {
  removed: 'red',
  price_changed: 'orange',
  qty_changed: 'blue',
  price_and_qty_changed: 'orange',
};

function formatPrice(p: number | null | undefined): string {
  if (p == null) return '—';
  return `${p.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} \u20BD`;
}

function formatQty(q: number | null | undefined): string {
  if (q == null) return '—';
  return `${q} шт.`;
}

const CartValidationModal: React.FC<Props> = ({ open, changes, taskId, onClose, onResolved }) => {
  // Для каждого элемента: true = оставить, false = убрать
  const [decisions, setDecisions] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const c of changes) {
      const key = `${c.size_id}::${c.supplier_id}::${c.origin ?? ''}`;
      // Удалённые по умолчанию убираем, изменённые — оставляем
      init[key] = c.change_type !== 'removed';
    }
    return init;
  });
  const [loading, setLoading] = useState(false);

  const toggleDecision = (c: CartChangeItem) => {
    const key = `${c.size_id}::${c.supplier_id}::${c.origin ?? ''}`;
    setDecisions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const columns = useMemo(() => [
    {
      title: 'Поставщик',
      dataIndex: 'supplier_name',
      width: 100,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Товар',
      key: 'product',
      width: 200,
      render: (_: unknown, r: CartChangeItem) => (
        <Text style={{ fontSize: 12 }}>{r.product_name} {r.size_label}</Text>
      ),
    },
    {
      title: 'В корзине',
      dataIndex: 'cart_qty',
      width: 60,
      align: 'center' as const,
      render: (v: number) => <Text strong>{v} шт.</Text>,
    },
    {
      title: 'Было',
      key: 'old',
      width: 120,
      render: (_: unknown, r: CartChangeItem) => (
        <div style={{ fontSize: 12 }}>
          <div>{formatPrice(r.old_price)}</div>
          <div style={{ color: '#8c8c8c' }}>{formatQty(r.old_available)}</div>
        </div>
      ),
    },
    {
      title: 'Стало',
      key: 'new',
      width: 120,
      render: (_: unknown, r: CartChangeItem) => {
        if (r.change_type === 'removed') {
          return <Text type="danger" style={{ fontSize: 12 }}>Удалён из прайса</Text>;
        }
        const priceUp = r.old_price != null && r.new_price != null && r.new_price > r.old_price;
        const priceDown = r.old_price != null && r.new_price != null && r.new_price < r.old_price;
        return (
          <div style={{ fontSize: 12 }}>
            <div style={{ color: priceUp ? '#ff4d4f' : priceDown ? '#52c41a' : undefined, fontWeight: 500 }}>
              {formatPrice(r.new_price)}
              {priceUp && ' \u2191'}
              {priceDown && ' \u2193'}
            </div>
            <div style={{ color: '#8c8c8c' }}>{formatQty(r.new_available)}</div>
          </div>
        );
      },
    },
    {
      title: 'Изменение',
      key: 'change_type',
      width: 100,
      render: (_: unknown, r: CartChangeItem) => (
        <Tag color={CHANGE_COLORS[r.change_type]}>{CHANGE_LABELS[r.change_type]}</Tag>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_: unknown, r: CartChangeItem) => {
        const key = `${r.size_id}::${r.supplier_id}::${r.origin ?? ''}`;
        const keep = decisions[key];
        if (r.change_type === 'removed') {
          return (
            <Button size="small" danger icon={<DeleteOutlined />} disabled>
              Убрать
            </Button>
          );
        }
        return keep ? (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<CheckOutlined />}
            onClick={() => toggleDecision(r)}
          >
            Оставить
          </Button>
        ) : (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => toggleDecision(r)}
          >
            Убрать
          </Button>
        );
      },
    },
  ], [decisions]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const body: CartResolveBody = { keep: [], remove: [], task_id: taskId };
      for (const c of changes) {
        const key = `${c.size_id}::${c.supplier_id}::${c.origin ?? ''}`;
        const item = { size_id: c.size_id, supplier_id: c.supplier_id, origin: c.origin };
        if (c.change_type === 'removed' || !decisions[key]) {
          body.remove.push(item);
        } else {
          body.keep.push(item);
        }
      }
      await oilOrdersApi.resolveCart(body);
      message.success('Корзина обновлена');
      onResolved();
    } catch {
      message.error('Ошибка при обновлении корзины');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={
        <Space>
          <WarningOutlined style={{ color: '#faad14' }} />
          <span>Изменения в прайсе ({changes.length} поз.)</span>
        </Space>
      }
      width={850}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" loading={loading} onClick={handleConfirm}>
            Подтвердить
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          После обновления прайса обнаружены изменения в позициях, добавленных в корзину.
          Проверьте и подтвердите.
        </Text>
      </div>
      <Table<CartChangeItem>
        dataSource={changes}
        columns={columns}
        rowKey={r => `${r.size_id}::${r.supplier_id}::${r.origin ?? ''}`}
        pagination={false}
        size="small"
        scroll={{ y: 350 }}
      />
    </Modal>
  );
};

export default CartValidationModal;
